/**
 * Utilidades para las próximas llegadas de mercancía (`product.proxima_llegada`):
 * [{ fecha: 'YYYY-MM-DD', cantidad, estado, colores?: [{ color, cantidad }] }]
 */

// Separador de miles con punto (2649 → 2.649). No se usa toLocaleString('es-CO')
// porque en español no agrupa números de 4 cifras.
export const formatUnits = (value) => {
  const n = Number(value);
  if (value === null || value === '' || !Number.isFinite(n)) return String(value ?? '');
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/** '2026-10-31' → '31 oct 2026' (month: 'short') o '31 de octubre de 2026' (month: 'long'). */
export const formatArrivalDate = (fecha, month = 'short') => {
  const date = new Date(`${fecha}T00:00:00`);
  if (month === 'long') {
    return date.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
  }
  // es-CO en corto da "31 de oct de 2026"; en tablas se lee mejor "31 oct 2026".
  const mes = date.toLocaleDateString('es-CO', { month: 'short' }).replace('.', '');
  return `${date.getDate()} ${mes} ${date.getFullYear()}`;
};

// Fecha de hoy en hora local como 'YYYY-MM-DD', comparable con `fecha`.
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Llegadas de hoy en adelante: entre un sync y otro pueden quedar fechas ya pasadas. */
export const getArrivals = (product) => {
  if (!Array.isArray(product?.proxima_llegada)) return [];
  const today = todayISO();
  return product.proxima_llegada.filter((a) => a?.fecha && a.fecha >= today);
};

/** ¿Alguna llegada trae el desglose por color? */
export const hasColorBreakdown = (arrivals) =>
  arrivals.some((a) => Array.isArray(a.colores) && a.colores.length > 0);

const normalizeColor = (color) => String(color || '').trim().toLowerCase();

/**
 * Agrupa las llegadas por color: Map(colorNormalizado → { color, total, envios: [{ fecha, cantidad }] }),
 * con los envíos ordenados por fecha.
 */
export const arrivalsByColor = (arrivals) => {
  const byColor = new Map();
  for (const arrival of arrivals) {
    for (const { color, cantidad } of arrival.colores || []) {
      const key = normalizeColor(color);
      if (!byColor.has(key)) byColor.set(key, { color, total: 0, envios: [] });
      const entry = byColor.get(key);
      entry.total += Number(cantidad) || 0;
      entry.envios.push({ fecha: arrival.fecha, cantidad });
    }
  }
  byColor.forEach((entry) => entry.envios.sort((a, b) => a.fecha.localeCompare(b.fecha)));
  return byColor;
};

export const findColorArrivals = (byColor, color) => byColor.get(normalizeColor(color)) || null;
